import {Component, computed, HostListener, inject, Input, OnInit, signal} from '@angular/core';

import {ColDef, GridOptions, RowClassParams} from 'ag-grid-community';

import {Store} from '@ngrx/store';

import {Observable, firstValueFrom, of} from 'rxjs';

import {GridUtils} from '../../../../shared-utilities/utils-old/grid-utils-old/grid-utils';

import {IDebtorDetails} from '../../../../features/quotes/models/debtors';

import {ModalControllerService} from '../../../shared-services/overlay/modal-controller.service';

import {

  SharedGridLoadingOverlayComponent,

} from '../shared-grid-loading-overlay/shared-grid-loading-overlay.component';

import {loadQuoteDebtors, beginNewQuote} from '../../../../features/quotes/store/quotes.actions';
import {
  createQuoteLocalDraftId,
  QUOTES_LOCAL_DRAFT_MAX_COUNT,
} from '../../../../features/quotes/models/quote-local-draft.interface';

import {selectIsQuoteDebtorsLoading, selectIsQuoteLocalDraftLimitReached, selectQuoteDebtors} from '../../../../features/quotes/store/quotes.selectors';

import {normalizeQuoteDebtor} from '../../../../features/quotes/utils/quote-debtor.utils';

import {isQuotesMobileLayout} from '../../../../features/quotes/utils/quotes-layout.utils';

import {gridColumnTypes} from '../../../../shared-utilities/models-old/ngp-report-grid/defaults/column-types/column-types';
import {SharedModalUtils} from '../../../shared-utils/shared-modal/shared-modal.utils';
import {IconColourEnum} from '../../../../shared-modules/shared-icons/models/icons-colour.enum';
import {IconsEnum} from '../../../../shared-modules/shared-icons/models/icons.enum';

import {

  SHARED_DEBTOR_SEARCH_DESKTOP_COL_DEFS,

  SHARED_DEBTOR_SEARCH_MOBILE_COL_DEFS,

} from '../shared-debtor-search/shared-debtor-search-col-defs';



export const SHARED_DEBTOR_SEARCH_MODAL_CSS = 'shared-debtor-search-modal-css';



type IDebtorWithExtras = IDebtorDetails & { debtorId?: string; isDebtorSelected?: boolean; account?: string };



/**

 * Shared modal for selecting a debtor. Can load debtors from the quotes feature store

 * or accept debtor data via inputs for use outside quotes.

 */

@Component({

  selector: 'app-shared-debtor-search-modal',

  templateUrl: './shared-debtor-search-modal.component.html',

  styleUrls: ['./shared-debtor-search-modal.component.scss'],

  standalone: false,

})

export class SharedDebtorSearchModalComponent implements OnInit {

  private readonly sharedModalUtils = inject(SharedModalUtils);

  @Input() title = 'Select Debtor';

  @Input() requireSelection = false;

  @Input() useQuotesDebtorStore = true;

  @Input() debtors: Record<string, IDebtorWithExtras> | null = null;

  @Input() isLoading = false;



  debtors$: Observable<Record<string, IDebtorWithExtras>> = of({});

  isDebtorsLoading$: Observable<boolean> = of(false);



  private readonly isMobile = signal<boolean>(isQuotesMobileLayout());



  protected readonly gridColDefs = computed<ColDef[]>(() =>

    this.isMobile() ? SHARED_DEBTOR_SEARCH_MOBILE_COL_DEFS : SHARED_DEBTOR_SEARCH_DESKTOP_COL_DEFS,

  );



  protected readonly gridOptions: GridOptions = {

    columnTypes: gridColumnTypes,

    colResizeDefault: 'shift',

    debug: false,

    editType: null,

    enterNavigatesVertically: true,

    enterNavigatesVerticallyAfterEdit: true,

    suppressAnimationFrame: true,

    enableCellTextSelection: false,

    multiSortKey: 'ctrl',

    rowBuffer: 100,

    pagination: true,

    rowHeight: 45,

    suppressScrollOnNewData: true,

    paginationAutoPageSize: false,

    paginationPageSize: 20,

    paginationPageSizeSelector: [10, 15, 20, 50, 100],

    suppressPaginationPanel: false,

    rowDragManaged: false,

    rowDragEntireRow: false,

    loadingOverlayComponent: SharedGridLoadingOverlayComponent,

    rowDragMultiRow: false,

    onFirstDataRendered: (params) => {
      params.api.sizeColumnsToFit();
    },

    onRowDataUpdated: (params) => {
      params.api.sizeColumnsToFit();
    },

    rowSelection: {

      mode: 'singleRow',

      checkboxes: false,

      enableClickSelection: true,

      enableSelectionWithoutKeys: true,

      isRowSelectable: (node) => !!node.data,

    },

    singleClickEdit: true,

    stopEditingWhenCellsLoseFocus: true,

    undoRedoCellEditing: false,

    undoRedoCellEditingLimit: 25,

    getRowStyle: (params: RowClassParams): Record<string, string> => {

      const lineColour = params.data?.lineColourValue as string | undefined;

      if (!lineColour) {

        return {};

      }

      return {

        backgroundColor: lineColour,

        color: GridUtils.getContrastColor(lineColour),

        fill: 'black',

      };

    },

  };



  constructor(

    private readonly store: Store,

    private readonly modalController: ModalControllerService,

  ) {}



  ngOnInit(): void {

    if (this.useQuotesDebtorStore) {

      this.store.dispatch(loadQuoteDebtors());

      this.debtors$ = this.store.select(selectQuoteDebtors);

      this.isDebtorsLoading$ = this.store.select(selectIsQuoteDebtorsLoading);

    }

  }



  @HostListener('window:resize')

  onResize(): void {

    this.isMobile.set(isQuotesMobileLayout());

  }



  protected async handleSelectedItems(debtorArray: IDebtorWithExtras[]): Promise<void> {
    if (!debtorArray?.length) {
      void this.modalController.dismiss();
      return;
    }

    if (this.useQuotesDebtorStore) {
      const limitReached = await firstValueFrom(this.store.select(selectIsQuoteLocalDraftLimitReached));
      if (limitReached) {
        await this.sharedModalUtils.createModal({
          modalTitleIcon: IconsEnum.infoSlabCircleOutline,
          modalTitleIconColor: IconColourEnum.primary,
          modalTitle: 'Local Draft Limit Reached',
          contextDescription: [
            `You can have at most ${QUOTES_LOCAL_DRAFT_MAX_COUNT} local drafts. Delete existing drafts before creating a new quote.`,
          ],
          buttonAccept: false,
          buttonAcceptText: '',
          buttonClose: true,
          buttonCloseText: 'OK',
          buttonCloseCross: false,
        });
        return;
      }

      const debtor = normalizeQuoteDebtor(debtorArray[0]);
      this.store.dispatch(beginNewQuote({
        debtor,
        draftId: createQuoteLocalDraftId(),
      }));
      void this.modalController.dismiss();
      return;
    }

    void this.modalController.dismiss(debtorArray);
  }

}


